import { useMemo, useState } from 'react';
import { Anchor } from '@mantine/core';
import { useQuery } from '@tanstack/react-query';
import { IoListOutline } from 'react-icons/io5';
import { Link } from 'react-router-dom';
import { OptionsTable } from '@components';
import type { OptionsTableColumn } from '@components';
import { QuietIconButton } from '@shared/ui';
import {
    sellerFinanceApi,
    type PlatformAdvertisingSellerRow,
} from '@modules/seller-finance/api/seller-finance.api';
import { SellerAdvertisingHistoryModal } from '@modules/seller-finance/components/SellerAdvertisingHistoryModal';
import { formatPrice } from '@shared/utils';

type AdvertisingTableRow = PlatformAdvertisingSellerRow & { id: number | string };

const TOTAL_ROW_ID = '__total-advertising';
const isTotal = (row: AdvertisingTableRow) => row.id === TOTAL_ROW_ID;
const cell = (row: AdvertisingTableRow, value: React.ReactNode) =>
    isTotal(row) ? <span className="seller-finance-amount">{value}</span> : value;

export function PlatformAdvertisingPanel({
    periodFrom,
    periodTo,
}: {
    periodFrom?: string;
    periodTo?: string;
}) {
    const { data, isLoading, isError, refetch } = useQuery({
        queryKey: ['seller-finance', 'platform-advertising', periodFrom, periodTo],
        queryFn: () => sellerFinanceApi.getPlatformAdvertising({ periodFrom, periodTo }),
    });
    const [historySeller, setHistorySeller] = useState<{
        sellerId: number;
        shopName: string;
    } | null>(null);

    const tableData = useMemo((): AdvertisingTableRow[] => {
        if (!data?.rows.length) return [];
        const rows: AdvertisingTableRow[] = data.rows.map((r) => ({ ...r, id: r.sellerId }));
        rows.push({
            id: TOTAL_ROW_ID,
            sellerId: 0,
            shopName: '',
            slug: '',
            entriesCount: 0,
            lastSpendAt: null,
            ...data.totals,
        });
        return rows;
    }, [data]);

    const columns = useMemo<OptionsTableColumn<AdvertisingTableRow>[]>(
        () => [
            {
                key: 'shopName',
                label: 'Продавец',
                render: (row) =>
                    isTotal(row) ? (
                        <span className="seller-finance-op-label">Итого</span>
                    ) : (
                        <Anchor
                            component={Link}
                            to={`/sellers/${row.sellerId}`}
                            state={{ from: 'seller-finance' }}
                            className="seller-finance-cell-link"
                        >
                            {row.shopName || `#${row.sellerId}`}
                        </Anchor>
                    ),
            },
            {
                key: 'spentRub',
                label: 'Потрачено',
                render: (row) => (
                    <span className="seller-finance-amount seller-finance-amount--plus">
                        {formatPrice(row.spentRub)}
                    </span>
                ),
            },
            {
                key: 'billedClicks',
                label: 'Кликов',
                render: (row) => cell(row, row.billedClicks),
            },
            {
                key: 'topUpRub',
                label: 'Пополнено',
                render: (row) => cell(row, formatPrice(row.topUpRub)),
            },
            {
                key: 'withdrawnRub',
                label: 'Выведено',
                render: (row) => cell(row, formatPrice(row.withdrawnRub)),
            },
            {
                key: 'balanceRub',
                label: 'Остаток сейчас',
                render: (row) => cell(row, formatPrice(row.balanceRub)),
            },
            {
                key: 'lastSpendAt',
                label: 'Последний клик',
                render: (row) =>
                    row.lastSpendAt ? (
                        new Date(row.lastSpendAt).toLocaleString('ru-RU')
                    ) : (
                        <span className="seller-finance-muted">—</span>
                    ),
            },
            {
                key: '_history',
                label: '',
                style: { width: 1, whiteSpace: 'nowrap' },
                render: (row) =>
                    isTotal(row) ? null : (
                        <QuietIconButton
                            icon={<IoListOutline size={16} />}
                            onClick={() =>
                                setHistorySeller({ sellerId: row.sellerId, shopName: row.shopName })
                            }
                        >
                            История
                        </QuietIconButton>
                    ),
            },
        ],
        [],
    );

    return (
        <section className="seller-dashboard-card seller-finance-card">
            <div className="seller-dashboard-card__head">
                <h2 className="seller-dashboard-card__title">
                    Реклама по продавцам
                    {periodFrom && periodTo ? ` ${periodFrom} — ${periodTo}` : ' (всё время)'}
                </h2>
            </div>
            <p className="seller-finance-note">
                «Потрачено» — списания за клики, это выручка платформы от рекламы. Пополнения — аванс
                продавца, выручкой не считаются.
            </p>
            <div className="seller-finance-table-wrap">
                <OptionsTable<AdvertisingTableRow>
                    columns={columns}
                    data={tableData}
                    loading={isLoading}
                    error={isError ? new Error('Не удалось загрузить рекламу') : undefined}
                    refetch={refetch}
                    emptyMessage="За период нет рекламных операций"
                />
            </div>
            <SellerAdvertisingHistoryModal
                seller={historySeller}
                onClose={() => setHistorySeller(null)}
            />
        </section>
    );
}
